import { CustomerManagement } from '../components/CustomerManagement';
import { FileText, AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Plus, ArrowRight, Euro, CreditCard, Zap, Archive } from 'lucide-react';
import { Link } from 'react-router';
import { useNavigate } from 'react-router';
import { useState, useEffect, useMemo } from 'react';
import { motion, MotionConfig, useReducedMotion } from 'motion/react';
import { PieChart, Pie, Cell } from 'recharts';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { useSubscriptionStatus } from '../context/SubscriptionContext';
import { PlanLimitModal } from '../components/PlanLimitModal';
import NachforderungenBlock from '../components/dashboard/NachforderungenBlock';
import { listNachforderungen, ueberholteNachforderungen, ampelFor } from '../lib/nachforderungenApi';
import { ampelBadge } from '../components/dashboard/ampel';
import RoiStatCard from '../components/RoiStatCard';
import { useCountUp } from '../lib/useCountUp';
import { fadeUp, staggerContainer } from '../lib/dashboardMotion';

/** Berater bestätigt "liegt bei BAFA, von uns hängt nichts mehr ab" (Ein-Klick-Knopf in
 *  "Bald auslaufende Beratungen") — ein normaler Berichts-Status wie jeder andere, kein
 *  eigenes Datenbankfeld. Fall Berg Energie, Kundenfeedback 14.10.2026: "In Bearbeitung" +
 *  abgelaufener Förderzeitraum ist mehrdeutig (liegengeblieben ODER bei BAFA) — das System
 *  kann das nicht automatisch unterscheiden, daher bewusst ein Klick statt Automatik. */
const STATUS_BEI_BAFA = 'Bei BAFA zur Prüfung';

interface ExpiringReport {
  id: number;
  kunde: string;
  berichtstitel: string;
  aktenzeichen: string;
  endDate: string | null;
  daysRemaining: number;
  remainingTime: { text: string; color: string };
  /** Echter Berichts-Status (z.B. "In Bearbeitung", "Inaussichtstellung erfolgt") — wird bei
   *  abgelaufenem Förderzeitraum statt eines pauschalen "In Prüfung" angezeigt, da Letzteres
   *  fälschlich einen bereits eingereichten VN suggerierte, auch wenn der Bericht laut Status
   *  noch gar nicht fertig war. */
  status: string;
  /** Gesetzt, wenn eine offene BAFA-Nachforderung zu diesem Vorgang vorliegt — hat
   *  Vorrang vor dem Förderzeitraum-Status, da sie unabhängig davon dringend ist. */
  rueckfrage?: { frist: string | null };
  /** Für den Klick auf den Eintrag — führt zu den Dokumenten im Vorgang (Kundenwunsch),
   *  nicht in den Berichts-Editor. */
  customerId: number | null;
  vorgangId: number | null;
}

interface RecentReport {
  id: number;
  kunde: string;
  berichtstitel: string;
  aktenzeichen: string;
  status: string;
  statusColor: string;
}

interface RecentCustomer {
  id: number;
  name: string;
  plz: string;
  contactPerson: string;
  customerId: number;
}

const formatDateShort = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

/** Nutzerwunsch 16.10.2026: Begrüßung soll nicht immer dieselbe Floskel sein — Auswahl per
 *  Tagesdatum "geseedet" (stabil über den Tag, wechselt aber täglich), statt bei jedem
 *  Reload neu zu würfeln. */
const GREETING_POOLS: Record<'morgen' | 'tag' | 'abend' | 'nacht', string[]> = {
  morgen: ['Guten Morgen', 'Moin', 'Schönen Start in den Tag', 'Hallo'],
  tag: ['Guten Tag', 'Hallo', 'Schön, dass du da bist', 'Willkommen zurück'],
  abend: ['Guten Abend', 'Hallo', 'Noch produktiv unterwegs'],
  nacht: ['Noch spät unterwegs', 'Guten Abend', 'Hallo Nachteule'],
};

const seededIndex = (seed: string, length: number): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 997;
  return hash % length;
};

// Modul-Ebene statt lokaler const-Funktion in Dashboard(): als lokale const hätte jeder
// Re-Render von Dashboard() (jeder RAF-Tick der useCountUp-Hooks, jeder Jahres-Stepper-Klick,
// jedes setAufgabenCounts aus NachforderungenBlock) eine neue Funktionsidentität erzeugt —
// React hätte das als anderen Komponenten-Typ behandelt und die Subtree (inkl. motion.div/
// Recharts-Pie) jedes Mal neu gemountet, wodurch Entrance- und Donut-Animation ständig
// erneut abgespielt wurden statt nur einmal beim ersten Laden (Cross-Task-Bug, finale
// Branch-Review 2026-10-23).
function StatPie({
  data,
  centerLabel,
  centerSub,
  darkMode,
  prefersReducedMotion,
}: {
  data: Array<{ name: string; value: number; color: string }>;
  centerLabel: string;
  centerSub?: string;
  darkMode: boolean;
  prefersReducedMotion: boolean;
}) {
  const hasAny = data.some(d => d.value > 0);
  const chartData = hasAny ? data : [{ name: '—', value: 1, color: darkMode ? '#334155' : '#e2e8f0' }];
  return (
    <div className="relative w-[110px] h-[110px] flex-shrink-0">
      <PieChart width={110} height={110}>
        <Pie
          data={chartData}
          dataKey="value"
          innerRadius={36}
          outerRadius={52}
          startAngle={90}
          endAngle={-270}
          stroke="none"
          isAnimationActive={!prefersReducedMotion}
          animationDuration={700}
          animationEasing="ease-out"
        >
          {chartData.map((entry, idx) => (
            <Cell key={idx} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className={`text-base font-semibold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {centerLabel}
        </span>
        {centerSub && (
          <span className={`text-[12px] leading-tight ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {centerSub}
          </span>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  icon,
  children,
  legend,
  headerRight,
  darkMode,
  prefersReducedMotion,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  legend: Array<{ color: string; label: string; value: string }>;
  headerRight?: React.ReactNode;
  darkMode: boolean;
  prefersReducedMotion: boolean;
}) {
  return (
    <motion.div variants={fadeUp(prefersReducedMotion)} className={`rounded-xl border shadow-sm p-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
          {icon}
        </div>
        <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
        {headerRight && <div className="ml-auto flex-shrink-0">{headerRight}</div>}
      </div>
      <div className="flex items-center gap-3">
        {children}
        <ul className="flex-1 min-w-0 space-y-1">
          {legend.map((l, i) => (
            <li key={i} className="flex items-center gap-2 text-[13px]">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
              <span className={`flex-1 truncate ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{l.label}</span>
              <span className={`font-medium tabular-nums ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{l.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { status } = useSubscriptionStatus();
  const { user } = useAuth();

  const [expiringReports, setExpiringReports] = useState<ExpiringReport[]>([]);
  const [bafaMarking, setBafaMarking] = useState<number | null>(null);
  const [archiving, setArchiving] = useState<number | null>(null);

  /** Ein-Klick-Bestätigung "liegt bei BAFA" (Kundenwunsch statt automatischer Erkennung,
   *  siehe STATUS_BEI_BAFA). Entfernt den Eintrag sofort lokal — ohne vollen Reload — statt
   *  auf den nächsten Poll zu warten. */
  const markBeiBafa = async (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    setBafaMarking(reportId);
    try {
      await api.patch(`/api/reports/${reportId}`, { status: STATUS_BEI_BAFA });
      setExpiringReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch {
      // Bleibt sichtbar — Berater kann es erneut versuchen.
    } finally {
      setBafaMarking(null);
    }
  };

  /** Ein-Klick-Archivieren direkt aus der Karte — für "verschlafene" Fälle, bei denen
   *  nichts mehr zu tun ist (Kundenwunsch). Derselbe Weg wie der Archivieren-Knopf im
   *  Vorgang selbst (archiviert den zugehörigen Vorgang, reversibel über den
   *  "Archivierte"-Filter in der Kundenliste). */
  const archiveFromCard = async (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    setArchiving(reportId);
    try {
      await api.patch(`/api/reports/${reportId}/archive`, { archived: true });
      setExpiringReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch {
      // Bleibt sichtbar — Berater kann es erneut versuchen.
    } finally {
      setArchiving(null);
    }
  };
  const [recentReports, setRecentReports] = useState<RecentReport[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<RecentCustomer[]>([]);
  const [allReports, setAllReports] = useState<Array<{ status: string; statusColor: string; endDate: string | null; hasRealEndDate: boolean; preis: number | null; mwstEnabled: boolean; foerderRate: number | null; startDate: string | null; customerId: number | null }>>([]);
  // Vorgänge, zu denen noch kein Bericht angelegt wurde (z.B. frisch gestellter Antrag) —
  // zählen in der "Berichte nach Status"-Kachel als "Neu" mit, sonst sind sie dort komplett
  // unsichtbar (Kundenfeedback 14.10.2026, Fall FG Maschinenbau: 2 Anträge gestellt, 0 Berichte).
  // Volle Objekte (nicht nur Anzahl) seit dem Jahresfilter der Kachel nötig — ein Datum pro
  // Vorgang, um ihn einem Jahr zuordnen zu können.
  const [vorgaengeOhneBericht, setVorgaengeOhneBericht] = useState<Array<{ id: number; datum: string | null }>>([]);
  /** Von NachforderungenBlock gemeldet — für die Kennzahl im Begrüßungskopf, siehe dort. */
  const [aufgabenCounts, setAufgabenCounts] = useState({ total: 0, ueberfaellig: 0 });
  const [limitModalCode, setLimitModalCode] = useState<'FREE_REPORT_LIMIT_REACHED' | 'MONTHLY_REPORT_LIMIT_REACHED' | null>(null);
  // Jahresfilter für "Berichte nach Status" — 'alle' entspricht dem bisherigen Verhalten.
  const [statusJahr, setStatusJahr] = useState<number | 'alle'>('alle');
  // Jahresfilter für die Umsatz-Kachel — 'alle' entspricht dem bisherigen Verhalten (Summe
  // über alle Jahre), Kundenwunsch 14.10.2026.
  const [umsatzJahr, setUmsatzJahr] = useState<number | 'alle'>('alle');
  // Für Count-up-Animation der Stat-Kacheln: wird einmalig true, sobald die erste
  // Dashboard-Datenladung (Promise.all unten) abgeschlossen ist — egal ob erfolgreich
  // oder fehlgeschlagen, sonst hängt die Animation bei einem Fetch-Fehler ewig bei 0.
  const [dataReady, setDataReady] = useState(false);

  const isAtReportLimit = status
    ? status.reportUsage.used >= status.reportUsage.limit && status.reportUsage.credits === 0
    : false;

  const handleNewReport = () => {
    if (isAtReportLimit && status) {
      setLimitModalCode(status.reportUsage.mode === 'total'
        ? 'FREE_REPORT_LIMIT_REACHED'
        : 'MONTHLY_REPORT_LIMIT_REACHED');
      return;
    }
    navigate('/editor');
  };

  useEffect(() => {
    Promise.all([
      api.get<any[]>('/api/reports'),
      listNachforderungen().catch(() => []),
      api.get<{ id: number; customerId: number; eingereichtAm: string | null; createdAt: string }[]>('/api/vorgaenge').catch(() => []),
    ]).then(([allFetchedReports, nachforderungen, alleVorgaenge]) => {
      // Archivierte Vorgänge (r.archived, vom Backend aus dem Vorgang abgeleitet) gehören
      // nicht aufs Dashboard — analog zu Kunden (Zeile unten) und Nachforderungen/eigenen
      // Aufgaben, wo das bereits gefiltert wird.
      const reports = allFetchedReports.filter((r) => !r.archived);
      // /api/vorgaenge liefert bereits nur nicht-archivierte Vorgänge (Backend-Filter) —
      // davon die, zu denen noch GAR KEIN Bericht existiert (auch kein archivierter, sonst
      // zählte ein Vorgang mit rein-archiviertem Bericht fälschlich als "ohne Bericht").
      const vorgangIdsMitBericht = new Set(allFetchedReports.map((r) => r.vorgangId).filter((v) => v != null));
      setVorgaengeOhneBericht(
        alleVorgaenge
          .filter((v) => !vorgangIdsMitBericht.has(v.id))
          .map((v) => ({ id: v.id, datum: v.eingereichtAm || v.createdAt || null })),
      );
      setAllReports(reports.map((r) => {
        const s = r.status || 'Neu';
        // "Abgeschlossen"-Kachel/Verbucht zählen nur, was tatsächlich ausgezahlt wurde
        // (Zahlung erfolgt/Ausgezahlt) — ein Bericht mit reinem Status "Abgeschlossen" (Report
        // fertig, aber BAFA hat noch nicht gezahlt) ist kein verbuchtes Honorar (Kundenfeedback
        // 14.10.2026).
        const statusColor =
          s === 'Ausgezahlt' || s === 'Zahlung erfolgt' ? 'green' :
          s === 'Abgelehnt' ? 'red' :
          s === 'Neu' ? 'blue' : 'yellow';
        return { status: s, statusColor, endDate: r.endDate || null, hasRealEndDate: !!r.endDate, preis: r.preis ?? null, mwstEnabled: !!r.mwstEnabled, foerderRate: r.foerderRate ?? null, startDate: r.startDate || null, customerId: r.customerId ?? null };
      }));
      const today = new Date();

      // Offene Nachforderungen je Vorgang (früheste Frist, falls mehrere) — unabhängig vom
      // Förderzeitraum: eine laufende BAFA-Rückfrage ist immer dringend.
      //
      // Kommt zu einem Vorgang eine NEUERE Nachforderung, ist die ältere darin aufgegangen
      // (die BAFA mahnt die akzeptierten Punkte nicht erneut an). Ihre — meist längst
      // abgelaufene — Frist darf hier nicht mehr zählen: Sonst zeigte die Kachel weiter das
      // alte Datum, obwohl die Frist verlängert wurde (Utz: 27.07. statt 11.08.).
      const aufgenommeneNf = ueberholteNachforderungen(nachforderungen);
      const offeneRueckfrageByVorgang = new Map<number, string | null>();
      for (const nf of nachforderungen) {
        if (nf.status === 'erledigt' || nf.vorgangId == null || aufgenommeneNf.has(nf.id)) continue;
        const bisher = offeneRueckfrageByVorgang.get(nf.vorgangId);
        if (bisher === undefined) {
          offeneRueckfrageByVorgang.set(nf.vorgangId, nf.frist ?? null);
        } else if (nf.frist && (!bisher || new Date(nf.frist) < new Date(bisher))) {
          offeneRueckfrageByVorgang.set(nf.vorgangId, nf.frist);
        }
      }

      const expiring: ExpiringReport[] = [];
      // Erledigte/ausgezahlte/abgelehnte Beratungen "laufen" nicht mehr aus → nicht anzeigen
      // (deckt sich mit der grün/erledigt-Statuslogik: Abgeschlossen/Ausgezahlt/Zahlung erfolgt).
      const DONE_STATUSES = ['Abgeschlossen', 'Ausgezahlt', 'Zahlung erfolgt', 'Abgelehnt'];
      for (const r of reports) {
        if (DONE_STATUSES.includes(r.status)) continue;
        const hatRueckfrage = r.vorgangId != null && offeneRueckfrageByVorgang.has(r.vorgangId);
        const rueckfrageFrist = hatRueckfrage ? (offeneRueckfrageByVorgang.get(r.vorgangId) ?? null) : null;
        // Eine Rückfrage mit viel Zeit (Ampel grün, >7 Tage) ist noch nicht dringend — sie soll
        // dann NICHT in dieser Karte auftauchen, nur weil sie offen ist (Kundenfeedback zum
        // Fall Ionis/1307141: "wenn noch im grünen Bereich, sollte es nicht erscheinen").
        // 'pruefen' (keine Frist bekannt) zählt weiterhin als dringend genug.
        const rueckfrageDringend = hatRueckfrage && ampelFor(rueckfrageFrist) !== 'gruen';
        // Berater hat bestätigt "liegt bei BAFA, nichts zu tun" (Ein-Klick-Knopf unten) —
        // bleibt trotz abgelaufenem Förderzeitraum draußen, AUSSER es kommt doch wieder eine
        // dringende Rückfrage rein (Fall Berg Energie, Kundenfeedback 14.10.2026).
        if (r.status === STATUS_BEI_BAFA && !rueckfrageDringend) continue;

        let diffDays: number | null = null;
        if (r.endDate) {
          const end = new Date(r.endDate);
          diffDays = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        }
        const qualifiziertViaFoerderzeitraum = diffDays !== null && diffDays <= 60;
        if (!qualifiziertViaFoerderzeitraum && !rueckfrageDringend) continue;

        const remainingTime = diffDays === null
          ? { text: '', color: 'orange' }
          : diffDays <= 0
          ? { text: 'Abgelaufen', color: 'red' }
          : diffDays <= 28
          ? { text: `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'}`, color: 'red' }
          : { text: `${Math.round(diffDays / 30)} ${Math.round(diffDays / 30) === 1 ? 'Monat' : 'Monate'}`, color: 'orange' };
        expiring.push({
          id: r.id,
          kunde: r.customer?.name || 'Unbekannt',
          berichtstitel: r.berichtstitel || '',
          aktenzeichen: r.aktenzeichen || '',
          endDate: r.endDate || null,
          daysRemaining: diffDays ?? Infinity,
          remainingTime,
          status: r.status || 'Neu',
          rueckfrage: hatRueckfrage ? { frist: rueckfrageFrist } : undefined,
          customerId: r.customerId ?? null,
          vorgangId: r.vorgangId ?? null,
        });
      }
      // Sortierung nach der jeweils maßgeblichen Dringlichkeit: Rückfrage-Frist, sonst
      // Förderzeitraum-Ende; ohne Datum ans Ende.
      const sortZeit = (r: ExpiringReport): number => {
        const iso = r.rueckfrage ? r.rueckfrage.frist : r.endDate;
        return iso ? new Date(iso).getTime() : Number.MAX_SAFE_INTEGER;
      };
      expiring.sort((a, b) => sortZeit(a) - sortZeit(b));
      setExpiringReports(expiring);

      setRecentReports(
        reports.slice(0, 5).map((r) => {
          const s = r.status || 'Neu';
          const statusColor =
            s === 'Abgeschlossen' || s === 'Ausgezahlt' || s === 'Zahlung erfolgt' ? 'green' :
            s === 'Abgelehnt' ? 'red' :
            s === 'Neu' ? 'blue' : 'yellow';
          return {
            id: r.id,
            kunde: r.customer?.name || 'Unbekannt',
            berichtstitel: r.berichtstitel || '',
            aktenzeichen: r.aktenzeichen || '',
            status: s,
            statusColor,
          };
        }),
      );
      setDataReady(true);
    }).catch(() => { setDataReady(true); });

    api.get<any[]>('/api/customers').then((customers) => {
      setRecentCustomers(
        customers.filter((c) => !c.archived).slice(0, 5).map((c) => ({
          id: c.id,
          name: c.name || 'Unbekannt',
          plz: c.plz || '',
          contactPerson: [c.vorname, c.nachname].filter(Boolean).join(' ') || c.contactPerson || '',
          customerId: c.id,
        })),
      );
    }).catch(() => {});
  }, []);


  // ── Dashboard-Statistik-Kacheln ────────────────────────────────────────
  // Jahre, zu denen es Daten für "Berichte nach Status" gibt (Berichte per startDate,
  // Vorgänge ohne Bericht per eingereichtAm/createdAt) — Kundenwunsch 14.10.2026: wie bei
  // Umsatz/ROI nach "Gesamt" und einzelnen Jahren umschaltbar.
  const statusJahre = useMemo(() => {
    const jahre = new Set<number>();
    for (const r of allReports) {
      if (r.startDate) jahre.add(new Date(r.startDate).getFullYear());
    }
    for (const v of vorgaengeOhneBericht) {
      if (v.datum) jahre.add(new Date(v.datum).getFullYear());
    }
    return [...jahre].sort((a, b) => b - a);
  }, [allReports, vorgaengeOhneBericht]);
  const reportStatusData = useMemo(() => {
    const reportsGefiltert = statusJahr === 'alle'
      ? allReports
      : allReports.filter(r => r.startDate && new Date(r.startDate).getFullYear() === statusJahr);
    const vorgaengeGefiltert = statusJahr === 'alle'
      ? vorgaengeOhneBericht
      : vorgaengeOhneBericht.filter(v => v.datum && new Date(v.datum).getFullYear() === statusJahr);
    const buckets = { green: 0, yellow: 0, blue: 0, kritisch: 0, beiBafa: 0 };
    for (const r of reportsGefiltert) {
      if (r.statusColor === 'green') { buckets.green++; continue; }
      // "Bei BAFA zur Prüfung" (Ein-Klick-Knopf im Dashboard) ist explizit "nichts zu tun" —
      // zählt weder als Kritisch (obwohl der alte Förderzeitraum oft längst abgelaufen ist)
      // noch als In Bearbeitung, sondern eigene neutrale Kategorie. Sonst zeigte diese Kachel
      // für genau bestätigte "nichts zu tun"-Fälle fälschlich rote Alarmfarbe (Kundenfeedback
      // 14.10.2026, Fall Berg Energie: Förderzeitraum 68/107 Tage überfällig).
      if (r.status === STATUS_BEI_BAFA) { buckets.beiBafa++; continue; }
      const isKritisch = r.hasRealEndDate && r.endDate &&
        Math.ceil((new Date(r.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) <= 28;
      if (isKritisch) { buckets.kritisch++; continue; }
      if (r.statusColor === 'yellow') { buckets.yellow++; continue; }
      buckets.blue++;
    }
    // Vorgänge ohne Bericht (z.B. frisch gestellter Antrag) zählen als "Neu" mit — sonst
    // unsichtbar in dieser Kachel (Kundenfeedback 14.10.2026).
    buckets.blue += vorgaengeGefiltert.length;
    return [
      { name: 'Abgeschlossen', value: buckets.green, color: '#22c55e' },
      { name: 'In Bearbeitung', value: buckets.yellow, color: '#eab308' },
      { name: 'Neu', value: buckets.blue, color: '#3b82f6' },
      { name: 'Kritisch', value: buckets.kritisch, color: '#ef4444' },
      { name: 'BAFA-Prüfung', value: buckets.beiBafa, color: '#94a3b8' },
    ];
  }, [allReports, vorgaengeOhneBericht, statusJahr]);
  const totalReports = reportStatusData.reduce((sum, d) => sum + d.value, 0);

  // ── Begrüßungskopf: Tageszeit-Gruß + relevante Kennzahl statt Floskel ──────────────
  const now = new Date();
  const greetingPool = GREETING_POOLS[
    now.getHours() < 5 ? 'nacht' : now.getHours() < 11 ? 'morgen' : now.getHours() < 17 ? 'tag' : now.getHours() < 22 ? 'abend' : 'nacht'
  ];
  const greeting = greetingPool[seededIndex(now.toISOString().slice(0, 10) + (user?.id ?? ''), greetingPool.length)];
  const firstName = (user?.contactPerson || '').split(' ')[0];
  const dateLabel = now.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  const greetingSubtext = useMemo(() => {
    // Höchste Priorität: was auch in "Zu erledigen" rot/"Überfällig" markiert ist
    // (Nachforderungen + eigene + abgeleitete Aufgaben, siehe aufgabenCounts/NachforderungenBlock)
    // — das ist für den Berater greifbarer als der reine Förderzeitraum und wurde als
    // Widerspruch gemeldet: "alles im grünen Bereich", obwohl dort 5 rote Karten standen.
    if (aufgabenCounts.ueberfaellig > 0) {
      return `${aufgabenCounts.ueberfaellig} überfällige Aufgabe${aufgabenCounts.ueberfaellig === 1 ? '' : 'n'} in „Zu erledigen" wartet auf dich.`;
    }
    const kritisch = expiringReports.filter(r => !r.rueckfrage && r.remainingTime.color === 'red').length;
    if (kritisch > 0) {
      return `${kritisch} Beratung${kritisch === 1 ? '' : 'en'} läuft in Kürze aus — ein guter Moment zum Dranbleiben.`;
    }
    const positivePool = [
      'Keine dringenden Fristen offen — alles im grünen Bereich.',
      totalReports > 0 ? `${totalReports} Berichte laufen gerade, nichts Dringendes liegt an.` : 'Keine dringenden Fristen offen.',
      'Guter Zeitpunkt, um eine neue Beratung anzustoßen.',
    ];
    return positivePool[seededIndex(now.toISOString().slice(0, 10) + 'subtext' + (user?.id ?? ''), positivePool.length)];
  }, [expiringReports, totalReports, user?.id, aufgabenCounts]);

  const quotaUsed = status?.reportUsage?.used ?? 0;
  const quotaLimit = status?.reportUsage?.limit ?? 0;
  const quotaRemaining = Math.max(quotaLimit - quotaUsed, 0);
  const quotaData = [
    { name: 'Genutzt', value: quotaUsed, color: '#3b82f6' },
    { name: 'Verfügbar', value: quotaRemaining, color: darkMode ? '#334155' : '#e2e8f0' },
  ];

  const customerRevenueMap = useMemo(() => {
    const map: Record<number, number> = {};
    for (const r of allReports) {
      if (r.statusColor === 'red' || !r.customerId || !r.preis) continue;
      const netto = r.mwstEnabled ? r.preis / 1.19 : r.preis;
      map[r.customerId] = (map[r.customerId] || 0) + netto;
    }
    return map;
  }, [allReports]);

  const calcNetto = (r: { preis: number | null; mwstEnabled: boolean }) =>
    r.preis ? (r.mwstEnabled ? r.preis / 1.19 : r.preis) : 0;
  // Jahre, zu denen es überhaupt Berichte mit Beratungszeitraum-Start gibt — nur die stehen
  // im Stepper zur Auswahl (absteigend, neuestes Jahr zuerst).
  const umsatzJahre = useMemo(() => {
    const jahre = new Set<number>();
    for (const r of allReports) {
      if (r.startDate) jahre.add(new Date(r.startDate).getFullYear());
    }
    return [...jahre].sort((a, b) => b - a);
  }, [allReports]);
  const umsatzReportsGefiltert = umsatzJahr === 'alle'
    ? allReports
    : allReports.filter(r => r.startDate && new Date(r.startDate).getFullYear() === umsatzJahr);
  // Verbucht = nur tatsächlich ausgezahlt (grün); "In Bearbeitung" (gelb) ist noch nicht
  // bezahlt und gehört zu Erwartet, nicht zu Verbucht (Kundenfeedback 14.10.2026: Verbucht
  // enthielt bislang auch unbezahlte gelbe Berichte, dadurch Summe höher als 23×Honorar).
  const umsatzVerbucht = umsatzReportsGefiltert
    .filter(r => r.statusColor === 'green')
    .reduce((sum, r) => sum + calcNetto(r), 0);
  const umsatzErwartet = umsatzReportsGefiltert
    .filter(r => r.statusColor === 'yellow' || r.statusColor === 'blue')
    .reduce((sum, r) => sum + calcNetto(r), 0);
  const umsatzData = [
    { name: 'Verbucht', value: umsatzVerbucht, color: '#22c55e' },
    { name: 'Erwartet', value: umsatzErwartet, color: darkMode ? '#334155' : '#e2e8f0' },
  ];

  // Berichtskontingent ist unabhängig vom Promise.all-Fetch oben bereits verfügbar, sobald
  // der SubscriptionContext geladen hat — eigene Readiness statt auf dataReady zu warten.
  const quotaReady = status != null;
  const prefersReducedMotion = useReducedMotion();
  const totalReportsAnimated = useCountUp(totalReports, dataReady);
  const umsatzAnimated = useCountUp(Math.round(umsatzVerbucht + umsatzErwartet), dataReady);
  const quotaUsedAnimated = useCountUp(quotaUsed, quotaReady);

  const fmtEuro = (n: number) => n.toLocaleString('de-DE') + ' €';

  return (
    <div className="min-h-screen flex bg-slate-100 dark:bg-slate-900 overflow-x-hidden">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8">
          <MotionConfig reducedMotion="user">
          <motion.div variants={staggerContainer(0.1, prefersReducedMotion)} initial="hidden" animate="visible" className="w-full max-w-full">
            {/* Begrüßungskopf: Datum + variierender Gruß + relevante Kennzahl statt Floskel */}
            <div className="mb-6">
              <p className={`text-xs font-semibold tracking-wide uppercase ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {dateLabel}
              </p>
              <h1 className={`text-2xl md:text-3xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {greeting}{firstName ? `, ${firstName}` : ''}
              </h1>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {greetingSubtext}
              </p>
            </div>

            {/* Upgrade-Banner für Free-Nutzer */}
            {status?.effectivePlan === 'free' && (
              <motion.div variants={fadeUp(prefersReducedMotion)} className={`flex items-center justify-between gap-4 rounded-xl px-4 py-3 mb-6 ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <div className="flex items-center gap-3">
                  <Zap className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <p className={`text-sm ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                    Sie nutzen die <strong>Free-Version</strong> — 1 Testbericht inklusive. Wählen Sie einen Tarif, um weitere Berichte zu erstellen.
                  </p>
                </div>
                <Link
                  to="/preise"
                  className="flex-shrink-0 text-sm font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  Upgrade
                </Link>
              </motion.div>
            )}

            {/* Stats Tiles */}
            <motion.div variants={staggerContainer(0.1, prefersReducedMotion)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <StatCard
                darkMode={darkMode}
                prefersReducedMotion={prefersReducedMotion}
                title="Berichte nach Status"
                icon={<FileText className="w-4 h-4" />}
                headerRight={
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setStatusJahr(prev => {
                        const opts: Array<number | 'alle'> = ['alle', ...statusJahre];
                        const idx = opts.indexOf(prev);
                        return idx > 0 ? opts[idx - 1] : prev;
                      })}
                      disabled={statusJahr === 'alle'}
                      className={`w-6 h-6 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
                      aria-label="Vorheriges Jahr"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className={`text-xs font-medium tabular-nums w-14 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {statusJahr === 'alle' ? 'Gesamt' : statusJahr}
                    </span>
                    <button
                      type="button"
                      onClick={() => setStatusJahr(prev => {
                        const opts: Array<number | 'alle'> = ['alle', ...statusJahre];
                        const idx = opts.indexOf(prev);
                        return idx < opts.length - 1 ? opts[idx + 1] : prev;
                      })}
                      disabled={statusJahre.length === 0 || statusJahr === statusJahre[statusJahre.length - 1]}
                      className={`w-6 h-6 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
                      aria-label="Nächstes Jahr"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                }
                legend={reportStatusData.map(d => ({ color: d.color, label: d.name, value: String(d.value) }))}
              >
                <StatPie
                  data={reportStatusData}
                  centerLabel={String(Math.round(totalReportsAnimated))}
                  centerSub={totalReports === 1 ? 'Bericht' : 'Berichte'}
                  darkMode={darkMode}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </StatCard>

              <StatCard
                darkMode={darkMode}
                prefersReducedMotion={prefersReducedMotion}
                title="Umsatz"
                icon={<Euro className="w-4 h-4" />}
                headerRight={
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setUmsatzJahr(prev => {
                        const opts: Array<number | 'alle'> = ['alle', ...umsatzJahre];
                        const idx = opts.indexOf(prev);
                        return idx > 0 ? opts[idx - 1] : prev;
                      })}
                      disabled={umsatzJahr === 'alle'}
                      className={`w-6 h-6 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
                      aria-label="Vorheriges Jahr"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className={`text-xs font-medium tabular-nums w-14 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {umsatzJahr === 'alle' ? 'Gesamt' : umsatzJahr}
                    </span>
                    <button
                      type="button"
                      onClick={() => setUmsatzJahr(prev => {
                        const opts: Array<number | 'alle'> = ['alle', ...umsatzJahre];
                        const idx = opts.indexOf(prev);
                        return idx < opts.length - 1 ? opts[idx + 1] : prev;
                      })}
                      disabled={umsatzJahre.length === 0 || umsatzJahr === umsatzJahre[umsatzJahre.length - 1]}
                      className={`w-6 h-6 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
                      aria-label="Nächstes Jahr"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                }
                legend={[
                  { color: umsatzData[0].color, label: 'Verbucht', value: fmtEuro(Math.round(umsatzVerbucht)) },
                  { color: umsatzData[1].color, label: 'Erwartet', value: fmtEuro(Math.round(umsatzErwartet)) },
                ]}
              >
                <StatPie
                  data={umsatzData}
                  centerLabel={fmtEuro(Math.round(umsatzAnimated)).replace(' €', '')}
                  centerSub="€ netto"
                  darkMode={darkMode}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </StatCard>

              <StatCard
                darkMode={darkMode}
                prefersReducedMotion={prefersReducedMotion}
                title="Berichtskontingent"
                icon={<CreditCard className="w-4 h-4" />}
                legend={[
                  { color: quotaData[0].color, label: 'Genutzt', value: String(quotaUsed) },
                  { color: quotaData[1].color, label: 'Verfügbar', value: String(quotaRemaining) },
                ]}
              >
                <StatPie
                  data={quotaData}
                  centerLabel={`${Math.round(quotaUsedAnimated)}/${quotaLimit || '–'}`}
                  centerSub={status?.reportUsage?.mode === 'monthly' ? 'pro Monat' : 'gesamt'}
                  darkMode={darkMode}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </StatCard>

              <RoiStatCard darkMode={darkMode} allReports={allReports} ready={dataReady} />
            </motion.div>

            {/* Main Action Cards - Side by Side */}
            <motion.div variants={staggerContainer(0.12, prefersReducedMotion)} className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              {/* Create Report Card */}
              <motion.div variants={fadeUp(prefersReducedMotion)} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 sm:p-6 flex flex-col">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                      Berichtsverwaltung
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Starten Sie einen neuen Beratungsbericht für einen Kunden.
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={handleNewReport}
                      title="Neuer Bericht"
                      aria-label="Neuer Bericht"
                      className="w-12 h-12 aspect-square flex items-center justify-center bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                      Neueste Berichte
                    </h3>
                    {recentReports.length > 0 && (
                      <button
                        onClick={() => navigate('/reports')}
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline cursor-pointer"
                      >
                        Mehr
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {recentReports.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center flex-1">
                      Noch keine Berichte vorhanden.
                    </p>
                  ) : (
                    <motion.ul variants={staggerContainer(0.05, prefersReducedMotion)} className="space-y-2 flex-1">
                        {recentReports.map((r) => (
                          <motion.li
                            variants={fadeUp(prefersReducedMotion)}
                            key={r.id}
                            onClick={() => navigate(`/editor/${r.id}`)}
                            className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:shadow-sm transition cursor-pointer"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                    {r.kunde}
                                  </span>
                                  {r.aktenzeichen && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">
                                      {r.aktenzeichen}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
                                  {r.berichtstitel || '—'}
                                </p>
                              </div>
                              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[12px] font-medium shrink-0 ${
                                r.statusColor === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                r.statusColor === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                r.statusColor === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              }`}>
                                {r.status}
                              </span>
                            </div>
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </div>
              </motion.div>

              {/* Customer Management Card */}
              <motion.div variants={fadeUp(prefersReducedMotion)} className="h-full">
              <CustomerManagement
                darkMode={darkMode}
                isExpanded={false}
                onToggleExpanded={() => navigate('/customers')}
                onAddNew={() => navigate('/customers?new=1')}
                hideManageButton
                className="h-full"
              >
                <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                      Neueste Kunden
                    </h3>
                    {recentCustomers.length > 0 && (
                      <button
                        onClick={() => navigate('/customers')}
                        className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline cursor-pointer"
                      >
                        Mehr
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {recentCustomers.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
                      Noch keine Kunden vorhanden.
                    </p>
                  ) : (
                    <motion.ul variants={staggerContainer(0.05, prefersReducedMotion)} className="space-y-2">
                      {recentCustomers.map((c) => {
                        const rev = customerRevenueMap[c.customerId] || 0;
                        return (
                          <motion.li
                            variants={fadeUp(prefersReducedMotion)}
                            key={c.id}
                            onClick={() => navigate(`/customers?customer=${c.customerId}`)}
                            className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:shadow-sm transition cursor-pointer"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                  {c.name}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {[c.plz, c.contactPerson].filter(Boolean).join(' · ') || '—'}
                                </p>
                              </div>
                              {rev > 0 && (
                                <span className="text-xs font-medium text-green-600 dark:text-green-400 shrink-0">
                                  {fmtEuro(Math.round(rev))}
                                </span>
                              )}
                            </div>
                          </motion.li>
                        );
                      })}
                    </motion.ul>
                  )}
                </div>
              </CustomerManagement>
              </motion.div>

              {/* Fehlende Unterlagen (Nachforderungs-Tracker) */}
              <NachforderungenBlock darkMode={darkMode} onCountsChange={setAufgabenCounts} />
            </motion.div>

            {/* Bald auslaufende Beratungen */}
            {expiringReports.length > 0 && (
              <motion.div variants={fadeUp(prefersReducedMotion)} className="mb-6 md:mb-8">
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5" />
                      </div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white truncate">Bald auslaufende Beratungen &amp; offene Rückfragen</h2>
                    </div>
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-full px-2.5 py-1 flex-shrink-0">{expiringReports.length}</span>
                  </div>
                  <motion.ul variants={staggerContainer(0.04, prefersReducedMotion)} className="space-y-2">
                    {expiringReports.map((report, index) => {
                      const formatDate = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                      const isPruefung = report.daysRemaining <= 0;
                      const isCritical = report.daysRemaining <= 28;
                      const rueckfrage = report.rueckfrage;
                      // Dieselbe Ampel wie in "Zu erledigen" (ampelFor/ampelBadge) — vorher
                      // galt jede offene Rückfrage hier pauschal als dringend/rot, unabhängig
                      // von ihrer Frist. Dadurch zeigte dieselbe Frist (Ionis/1307141, 06.11.,
                      // ~3 Wochen entfernt) hier Rot ("Rückfrage"), in "Zu erledigen" aber Grün
                      // ("Im Plan") — widersprüchlich für dieselbe Information (Kundenfeedback).
                      const rueckfrageAmpel = rueckfrage ? ampelFor(rueckfrage.frist) : null;
                      const rueckfrageBadge = rueckfrage ? ampelBadge(rueckfrage.frist) : null;
                      const dotColorByAmpel: Record<string, string> = {
                        rot: 'bg-red-500', orange: 'bg-orange-400', pruefen: 'bg-yellow-400', gruen: 'bg-green-500',
                      };
                      const dotColor = rueckfrageAmpel ? dotColorByAmpel[rueckfrageAmpel] : (isCritical ? 'bg-red-500' : 'bg-orange-400');
                      const badgeCls = rueckfrageBadge
                        ? rueckfrageBadge.cls
                        : isPruefung
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : isCritical
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300';
                      // Bei abgelaufenem Förderzeitraum den ECHTEN Berichts-Status zeigen statt
                      // eines pauschalen "In Prüfung" — das suggerierte fälschlich einen bereits
                      // eingereichten VN, auch wenn der Bericht laut Status noch gar nicht fertig
                      // war (z.B. weiterhin "In Bearbeitung", teils Monate über dem Enddatum).
                      const badgeText = rueckfrageBadge ? rueckfrageBadge.text : isPruefung ? report.status : report.remainingTime.text;
                      // Explizit benennen, WORUM es bei der Frist geht — sonst wirkt eine
                      // Rückfrage-Frist wie ein auslaufender Beratungszeitraum, obwohl dieser
                      // (wie bei Ionis/1307141) noch Monate Luft haben kann (Kundenfeedback).
                      const dateLine = rueckfrage
                        ? `Rückfrage-Frist: ${rueckfrage.frist ? formatDate(rueckfrage.frist) : 'noch offen'}`
                        : report.endDate ? `Förderzeitraum endet ${formatDate(report.endDate)}` : '';
                      // Kundenwunsch: zu den Dokumenten im Vorgang (Kundenseite), nicht in den
                      // Berichts-Editor — gleicher Deep-Link wie der "Link kopieren"-Knopf im
                      // Vorgang selbst (VorgangAccordion). Fallback auf den Editor nur, falls
                      // customerId/vorgangId ausnahmsweise fehlen sollten.
                      const ziel = report.customerId != null && report.vorgangId != null
                        ? `/customers?customer=${report.customerId}&vorgang=${report.vorgangId}`
                        : `/editor/${report.id}`;
                      return (
                        <motion.li
                          variants={fadeUp(prefersReducedMotion)}
                          key={index}
                          onClick={() => navigate(ziel)}
                          className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:shadow-sm transition cursor-pointer"
                        >
                          <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {report.kunde}
                              {report.aktenzeichen && <span className="text-xs text-slate-400 dark:text-slate-500 font-normal"> · {report.aktenzeichen}</span>}
                            </div>
                            {dateLine && <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{dateLine}</div>}
                          </div>
                          <span className={`text-[13px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${badgeCls}`}>{badgeText}</span>
                          {/* Förderzeitraum abgelaufen, keine Rückfrage offen, Status noch nicht
                              bestätigt "bei BAFA" — mehrdeutig, ob liegengeblieben oder einfach
                              nur bei BAFA (Kundenfeedback: Automatik unmöglich, Berg Energie).
                              Eigener weißer Hintergrund + Schatten statt nur Rahmen — sonst
                              verschwimmen die Knöpfe optisch mit der ganzen (ebenfalls
                              klickbaren) Zeile und wirken nicht wie eigene Buttons
                              (Kundenfeedback). "Archivieren" daneben für "verschlafene" Fälle,
                              bei denen man den Vorgang einfach nicht mehr verfolgen will. */}
                          {isPruefung && !rueckfrage && (
                            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                              {report.status !== STATUS_BEI_BAFA && (
                                <button
                                  type="button"
                                  onClick={(e) => markBeiBafa(e, report.id)}
                                  disabled={bafaMarking === report.id || archiving === report.id}
                                  title="Von uns hängt nichts mehr ab — liegt gerade zur Prüfung bei der BAFA"
                                  className="text-[12px] font-medium px-2.5 py-1.5 rounded-md border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 whitespace-nowrap disabled:opacity-50"
                                >
                                  {bafaMarking === report.id ? '…' : 'Liegt bei BAFA'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => archiveFromCard(e, report.id)}
                                disabled={bafaMarking === report.id || archiving === report.id}
                                title="Vorgang archivieren — nicht mehr weiterverfolgen"
                                className="inline-flex items-center gap-1 text-[12px] font-medium px-2.5 py-1.5 rounded-md border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 whitespace-nowrap disabled:opacity-50"
                              >
                                <Archive className="w-3.5 h-3.5" />
                                {archiving === report.id ? '…' : 'Archivieren'}
                              </button>
                            </div>
                          )}
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                </div>
              </motion.div>
            )}
          </motion.div>
          </MotionConfig>
        </div>
      </div>
      <PlanLimitModal
        open={limitModalCode !== null}
        code={limitModalCode}
        onClose={() => setLimitModalCode(null)}
      />
    </div>
  );
}
